import { useCallback, useEffect, useRef, useState } from 'react'
import { isTopmostOverlay, OVERLAY_LAYER, registerOverlay, unregisterOverlay } from './overlayRegistry'

/**
 * The open/close contract a set of disclosure panels shares: Escape, a pointer press
 * outside, focus into the panel on open and back to the trigger on close, and one panel
 * open at a time. Built for the header's panels; since GRO-64 it also serves the
 * creatives filter bar's, which pass their own overlay layer.
 *
 * Scoped to header disclosures and named for it. `DetailPanel` is a side panel with a
 * click-dismiss backdrop and GRO-17 owns rebuilding it; sharing an abstraction across the
 * two before that task has run would fix the wrong shape in place
 * (design: docs/plans/2026-09-18-gro-20-client-account-panels-design.md).
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

export interface TriggerProps {
  ref: (node: HTMLElement | null) => void
  'aria-expanded': boolean
  'aria-controls': string
  /**
   * Pointer activation, primary button only. See `triggerProps` for why this is not
   * `onClick` alone, and why the button is checked here rather than left to the browser.
   */
  onPointerDown: (event: { button: number; isPrimary: boolean }) => void
  /** Stops `mousedown` focusing the trigger, which would take focus back off the panel. */
  onMouseDown: (event: { preventDefault: () => void }) => void
  /** Keyboard activation only: a click with no pointer behind it (`detail === 0`). */
  onClick: (event: { detail: number }) => void
}

export interface PanelProps {
  id: string
  ref: (node: HTMLElement | null) => void
  /** The focus target when the panel holds no focusable control, as the empty client's does. */
  tabIndex: -1
}

export interface DisclosurePanels {
  openPanel: string | null
  isOpen: (name: string) => boolean
  /** Close and return focus to the trigger: Escape, and selection inside a panel. */
  close: () => void
  /** Close without moving focus, so a press that landed on another control keeps it. */
  dismiss: () => void
  /**
   * Open a panel from a control outside its trigger, such as an empty state's button.
   * Focus moves into the panel the way a trigger's opening moves it.
   */
  open: (name: string) => void
  triggerProps: (name: string) => TriggerProps
  panelProps: (name: string) => PanelProps
}

export function useDisclosurePanel(
  idPrefix = 'header-panel',
  layer: number = OVERLAY_LAYER.headerDisclosure,
): DisclosurePanels {
  const [openPanel, setOpenPanel] = useState<string | null>(null)
  const triggers = useRef(new Map<string, HTMLElement | null>())
  const panels = useRef(new Map<string, HTMLElement | null>())

  const close = useCallback(() => {
    setOpenPanel((prev) => {
      if (prev !== null) triggers.current.get(prev)?.focus()
      return null
    })
  }, [])

  const dismiss = useCallback(() => setOpenPanel(null), [])

  /**
   * Where the press that is currently in progress landed.
   *
   * The press is what decides whether the panel is dismissed - a drag that starts outside
   * and ends inside must not leave it open - but the dismissal itself waits for the
   * release. Below the shell's breakpoint an open panel is a block in normal flow, so
   * removing it on `pointerdown` moved every header control below it out from under the
   * pointer and the release landed on whatever had shifted into that place: the language
   * control, the theme toggle, the period control and sign-out were all unreachable at
   * 390px while a panel was open. Dismissing on the release lets the control this press
   * was aimed at act first, and the panel closes immediately afterwards.
   *
   * The record is read once and cleared by whichever event ends the gesture, because a
   * press does not always reach a `click`: a secondary button ends in `auxclick`, a touch
   * that turns into a scroll ends in `pointercancel`, and a release outside the window
   * ends in nothing at all. Left set, the record outlived its gesture and was spent by the
   * next `click` to arrive - which, with no press in front of it, is a keyboard
   * activation, so Space on a checkbox inside the panel dismissed the panel under the
   * reader using it.
   */
  const pressedOutside = useRef(false)

  useEffect(() => {
    if (openPanel === null) return
    // The disclosure joins the overlay stack for as long as it is open, at the layer its
    // `z-index` puts it on. A detail panel opened over the page stays below it, so one
    // Escape closes only the disclosure and a second one closes the panel; a detail panel
    // opened while this one is up displaces it, through `dismiss` rather than `close`,
    // because the panel is about to take the focus and must not have it pulled back to
    // this trigger (GRO-17's overlay registry).
    const overlayId = `${idPrefix}:${openPanel}`
    registerOverlay(overlayId, layer, dismiss)
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isTopmostOverlay(overlayId)) close()
    }
    function isOutside(target: Node | null): boolean {
      if (target === null) return false
      if (panels.current.get(openPanel!)?.contains(target)) return false
      // A press on any disclosure trigger is that trigger's to handle, and it handles it
      // on the press itself. This is not an exemption from dismissal: pressing this
      // panel's trigger toggles it shut and pressing the other one opens the other, which
      // closes this one either way. What it avoids is the two handlers racing.
      for (const node of triggers.current.values()) {
        if (node?.contains(target)) return false
      }
      return true
    }
    function onPointerDown(event: Event) {
      pressedOutside.current = isOutside(event.target as Node | null)
    }
    /** A press is spent by the first event that ends it, whether or not it dismisses. */
    function takePress(): boolean {
      const outside = pressedOutside.current
      pressedOutside.current = false
      return outside
    }
    function onClick(event: MouseEvent) {
      // No control is exempt from dismissing the panel, and focus is not moved: a press
      // that lands on another header control leaves focus there rather than snapping it
      // back to the trigger. What the press/release split narrows is the event, not the
      // set of controls - the press decides, the release dismisses.
      //
      // `detail` is 0 for a click with no pointer behind it. That is a keyboard
      // activation or an assistive technology's synthetic click, neither of which is the
      // release of anything, so neither may dismiss on a record it did not set.
      if (!takePress() || event.detail === 0) return
      setOpenPanel(null)
    }
    // The presses that never reach a `click`, each still a press outside that the reader
    // meant to land somewhere else: a secondary or middle button, and a touch the browser
    // takes over as a scroll. Dismissing on the event that does arrive keeps the rule the
    // design wrote - a press outside closes the panel - for gestures a `click` never
    // reports. A release outside the window reports nothing at all, and there the panel
    // stays open until the next press.
    function onPressEnded() {
      if (takePress()) setOpenPanel(null)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('click', onClick)
    document.addEventListener('auxclick', onPressEnded)
    document.addEventListener('contextmenu', onPressEnded)
    document.addEventListener('pointercancel', onPressEnded)
    return () => {
      pressedOutside.current = false
      unregisterOverlay(overlayId)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('click', onClick)
      document.removeEventListener('auxclick', onPressEnded)
      document.removeEventListener('contextmenu', onPressEnded)
      document.removeEventListener('pointercancel', onPressEnded)
    }
  }, [openPanel, close, dismiss, idPrefix, layer])

  useEffect(() => {
    if (openPanel === null) return
    const panel = panels.current.get(openPanel)
    if (!panel) return
    const first = panel.querySelector<HTMLElement>(FOCUSABLE)
    // A radio group is one stop, and Tab enters it on the checked radio: opening onto the
    // first radio would put focus on a choice that is not the current one. The company
    // switcher is this shape, with the active company anywhere in its list (GRO-66).
    const checked = first instanceof HTMLInputElement && first.type === 'radio' && first.name
      ? panel.querySelector<HTMLElement>(`input[type="radio"][name="${CSS.escape(first.name)}"]:checked`)
      : null
    ;(checked ?? first ?? panel).focus()
  }, [openPanel])

  const panelId = useCallback((name: string) => `${idPrefix}-${name}`, [idPrefix])

  // One panel at a time: opening either closes the other, because the state is one slot.
  // Closing by its own trigger returns focus there, the way `close` does; opening leaves
  // focus to the effect above, which puts it inside the panel.
  const toggle = useCallback((name: string) => {
    setOpenPanel((prev) => {
      if (prev !== name) return name
      triggers.current.get(name)?.focus()
      return null
    })
  }, [])

  /**
   * The press that activated the calling control is outside every panel, so with another
   * panel open it is recorded as a dismissal, and the document's `click` listener spends it
   * after the control's own handler has run: the panel this call opened would close in the
   * same click. Consuming the press first makes the click open the panel and nothing else.
   * A keyboard activation never dismisses, so it needs nothing.
   */
  const open = useCallback((name: string) => {
    pressedOutside.current = false
    setOpenPanel(name)
  }, [])

  /**
   * A trigger activates on `pointerdown`, not on `click`.
   *
   * Below the shell's breakpoint an open panel is a block in normal flow, so dismissing it
   * moves every header control below it. With `click` activation the press on the other
   * trigger dismissed the open panel, the header jumped up under the pointer, and the
   * release landed on whatever had moved into that place - no click, no second panel, and
   * a reader at 390px had to tap twice to get from one panel to the other.
   *
   * `click` is kept for keyboard activation alone. Enter and Space on a button synthesize a
   * click with `detail === 0`, which is how a pointer-driven click is told from a
   * keyboard-driven one without listening for the keys.
   *
   * `mousedown`'s default action focuses the button it lands on, and it runs after
   * `pointerdown`. Left alone it would take focus straight back off the panel this press
   * just opened, so it is prevented and both directions move focus explicitly.
   *
   * `pointerdown` fires for every button, where `click` fired for the primary one alone,
   * so the button is checked here. Without it a right-press opened the panel and moved
   * focus into it while the browser's own context menu opened over the top.
   */
  const triggerProps = useCallback((name: string): TriggerProps => ({
    ref: (node) => { triggers.current.set(name, node) },
    'aria-expanded': openPanel === name,
    'aria-controls': panelId(name),
    onPointerDown: (event) => { if (event.button === 0 && event.isPrimary) toggle(name) },
    onMouseDown: (event) => event.preventDefault(),
    onClick: (event) => { if (event.detail === 0) toggle(name) },
  }), [openPanel, panelId, toggle])

  const panelProps = useCallback((name: string): PanelProps => ({
    id: panelId(name),
    ref: (node) => { panels.current.set(name, node) },
    tabIndex: -1,
  }), [panelId])

  return {
    openPanel,
    isOpen: (name) => openPanel === name,
    close,
    dismiss,
    open,
    triggerProps,
    panelProps,
  }
}
